import { useState, useCallback, useEffect } from "react";

import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

export function useCreditCards({ iboNumber }) {
  const { makeAuthedRequest } = Auth.useContainer();
  const { logError } = useLogger({ prefix: "useCreditCards" });
  const [canChangePrimary, setCanChangePrimary] = useState(true);
  const [creditCards, setCreditCards] = useState(null);
  const [statusOptions, setStatusOptions] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [requestId, setRequestId] = useState(0);

  // Fetch card status options on mount.
  useEffect(() => {
    makeAuthedRequest(`/v1/membership/cards`)
      .then((res) => res.json())
      .then(setStatusOptions)
      .catch(logError);
  }, []);

  // Fetch IBO cards on mount and when requestId is incremented.
  useEffect(() => {
    if (requestId === 0 && creditCards != null) return;

    setIsWaiting(true);

    // Fetch and save the sorted list of cards.
    makeAuthedRequest(`/v1/membership/cards/${iboNumber}`)
      .then((res) => res.json())
      .then((res) => {
        setCreditCards(sortCards(res.cards));
        setCanChangePrimary(!res.hasPendingMembershipTransactions);
      })
      .catch(logError)
      .finally(() => {
        setIsWaiting(false);
      });
  }, [requestId]);

  // Trigger a card refresh by incrementing requestId.
  const refresh = useCallback(() => {
    setRequestId(requestId + 1);
  }, [requestId]);

  const makePrimary = useCallback(
    async (cardId) => {
      return makeAuthedRequest(`/v1/membership/cards/${iboNumber}`, {
        method: "PUT",
        body: JSON.stringify({
          cardId,
          setPrimary: true,
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const err = await res.json();
            throw new Error(err.message);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => refresh());
    },
    [refresh]
  );

  const updateStatus = useCallback(
    async (cardId, newStatus) => {
      return makeAuthedRequest(`/v1/membership/cards/${iboNumber}`, {
        method: "PUT",
        body: JSON.stringify({
          cardId,
          setPrimary: false,
          newStatus,
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            return res.text();
          } else {
            const err = await res.json();
            throw new Error(err.message);
          }
        })
        .catch(logError)
        .finally(() => refresh());
    },
    [refresh]
  );

  return {
    creditCards,
    refresh,
    makePrimary,
    updateStatus,
    isWaiting: isWaiting || statusOptions == null,
    statusOptions,
    canChangePrimary,
  };
}

function sortCards(cards) {
  // I really was not happy with other options for sorting.
  // The issue with this is if any of the types get depricated (like "active")
  // things will start to not order at all. for now this function takes in
  // the current list anf then puts primary at top along with the other active cards.
  // after that It puts the next types that I thought may be important or used offten.
  // then it just tosses the rest in at the end.

  let primary = [];
  let active = [];
  let inactive = [];
  let expired = [];
  let deleted = [];
  let other = [];

  cards.forEach((card) => {
    switch (card.status) {
      case "Active":
        if (card.isPrimary) {
          primary.push(card);
        } else {
          active.push(card);
        }
        break;
      case "Inactive":
        inactive.push(card);
        break;
      case "Expired":
        expired.push(card);
        break;
      case "Deleted":
        deleted.push(card);
        break;
      default:
        other.push(card);
    }
  });

  active.sort((a, b) =>
    (a.isPrimary === true) > (b.isPrimary === false) ? 1 : -1
  );

  return [...primary, ...active, ...inactive, ...expired, ...deleted, ...other];
}
